import React, { useCallback, useEffect, useMemo } from 'react';
import type { Campaign, ScheduleGet } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import {
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useScheduleVariant } from 'c-main/Components/Schedule/Dsp/ScheduleVariantContext';
import { cloudDownloadIcon, deleteIcon, editIcon, refreshIcon } from 'c-main/icons';
import { Alert } from 'c-components';
import {
    AddToQueue,
    CheckCircleOutlined,
    ExpandLess,
    ExpandMore,
    RemoveFromQueue,
    Warning,
    Error,
} from '@mui/icons-material';
import { scheduleName } from 'c-main/Components/Schedule/lib';

import useNewCampaignSchedule from 'c-main/Components/Schedule/useNewCampaignSchedule';
import type { UseBoolean } from 'react-hanger';
import { useBoolean } from 'react-hanger';
import { useAPIClientRequest } from 'c-hooks';
import { useEntityData } from 'c-data';
import ApiClient from 'c-data/apiClient';
import to from 'await-to-js';
import { NetworkRequestState } from 'c-data-layer';

import ScheduleRuleSummary from './ScheduleRuleSummary';
import { campaignDateBounds, ruleFallsOutsideCampaign } from '../ScheduleForm/scheduleFormLib';

type Props = {
    campaignId: number;
    schedule: ScheduleGet;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    onScreensClicked: () => void;
    onDetachClicked: () => void;
    setId: (id: number) => void;
    collapseAll?: UseBoolean;
};

const updatePerms = [
    PermissionName.UniliveapiSchedulesUpdate,
    PermissionName.UniliveapiSchedulesCreate,
    PermissionName.UniliveapiSchedulesDestroy,
    PermissionName.UniliveapiRulesCreate,
    PermissionName.UniliveapiRulesUpdate,
    PermissionName.UniliveapiRulesDestroy,
];

const deletePerms = [
    PermissionName.UniliveapiSchedulesDestroy,
    PermissionName.UniliveapiRulesDestroy,
];

const attachPerms = [
    PermissionName.UniliveapiSchedulesCreate,
    PermissionName.UniliveapiSchedulesRead,
    PermissionName.UniliveapiSchedulesUpdate,
    PermissionName.UniliveapiSchedulesDestroy,
];

const ScheduleListItem: React.FC<Props> = ({
    campaignId,
    schedule,
    onEdit,
    onDelete,
    onScreensClicked,
    onDetachClicked,
    setId,
    collapseAll,
}) => {
    const { expandedScheduleId, setExpandedScheduleId } = useNewCampaignSchedule(campaignId);
    const collapsed = useBoolean(false);
    const focusRef = React.useRef<HTMLDivElement>(null);
    const expanded = useMemo(
        () => expandedScheduleId === schedule.id,
        [expandedScheduleId, schedule.id],
    );

    const {
        getSchedules,
        scheduleSearch,
        schedulesPerPage,
        schedulesCurrentPage,
        selected,
        selectedScreens,
        selectedDisplays,
        popDisplaysSelected,
    } = useNewCampaignSchedule(campaignId);

    useEffect(() => {
        if (expanded) {
            collapsed.setTrue();
            focusRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [collapsed, expanded, expandedScheduleId, schedule.id]);

    useEffect(() => {
        if (collapseAll.value) {
            collapsed.setFalse();
            collapseAll.setFalse();
        }
    }, [collapseAll, collapsed]);

    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id: campaignId });
    const campaignDates = useMemo(
        () => campaignDateBounds(campaign?.date_start, campaign?.date_end),
        [campaign?.date_start, campaign?.date_end],
    );
    const rules = useMemo(
        () => (
            <Stack>
                {schedule?.rules?.map(rule => (
                    <Box key={rule.id}>
                        <ScheduleRuleSummary
                            rule={rule}
                            outsideCampaign={ruleFallsOutsideCampaign(rule, campaignDates)}
                        />
                    </Box>
                ))}
            </Stack>
        ),
        [schedule.rules, campaignDates],
    );
    const allSelected = useMemo(
        () => selected.concat(selectedScreens, selectedDisplays, popDisplaysSelected),
        [selected, selectedDisplays, selectedScreens, popDisplaysSelected],
    );

    // UL-7912 -- on a DSP campaign the selection is the ticked insertion orders, owned by the DSP
    // container, and ＋/－ act on them directly. The direct page never provides the context, so
    // for it these are exactly the values below were before.
    const scheduleVariant = useScheduleVariant();
    const isDsp = scheduleVariant.variant === 'dsp';
    const selectedCount = isDsp ? scheduleVariant.selectedCount : allSelected.length;
    const attachPermissions =
        isDsp && scheduleVariant.attachPermissions
            ? scheduleVariant.attachPermissions
            : attachPerms;

    const onEditClicked = useCallback(() => {
        onEdit(schedule.id);
    }, [onEdit, schedule.id]);
    const onDeleteClicked = useCallback(() => {
        onDelete(schedule.id);
    }, [onDelete, schedule.id]);

    const t = useCommonTranslation();

    const hasRuleOutsideCampaign = useMemo(
        () => (schedule.rules ?? []).some(rule => ruleFallsOutsideCampaign(rule, campaignDates)),
        [schedule.rules, campaignDates],
    );
    const header = useMemo(
        () => (
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                >
                    {scheduleName(schedule, t)}
                </Typography>
                {hasRuleOutsideCampaign && (
                    <Tooltip
                        title={t(
                            'Modules.Main.Campaigns.CampaignSchedule.Form.ruleOutsideCampaign',
                        )}
                    >
                        <Warning
                            color="error"
                            fontSize="small"
                            aria-label={t(
                                'Modules.Main.Campaigns.CampaignSchedule.Form.ruleOutsideCampaign',
                            )}
                        />
                    </Tooltip>
                )}
            </Stack>
        ),
        [schedule, t, hasRuleOutsideCampaign],
    );
    const attachClick = useCallback(() => {
        if (isDsp) {
            scheduleVariant.onAttach?.(schedule.id);
            return;
        }
        setId(schedule.id);
        onScreensClicked();
    }, [isDsp, scheduleVariant, onScreensClicked, schedule.id, setId]);

    const detachClick = useCallback(() => {
        if (isDsp) {
            scheduleVariant.onDetach?.(schedule.id);
            return;
        }
        setId(schedule.id);
        onDetachClicked();
    }, [isDsp, scheduleVariant, onDetachClicked, schedule.id, setId]);
    const { start } = useAPIClientRequest(ApiClient.Entities.Schedule.downloadSite);

    const handleDownload = useCallback(async () => {
        const [error, response] = await to(start(campaignId, schedule.id));
        if (error) {
            // eslint-disable-next-line no-console -- the download has no error UI; keep the failure visible for debugging
            console.error('Error during CSV download', error);
            return;
        }
        const csvData = response?.data;
        if (csvData) {
            const blob = new Blob([String(csvData)], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${schedule.name}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    }, [campaignId, schedule.id, schedule.name, start]);
    const {
        start: revStart,
        data,
        error,
        requestState,
        isLoading,
    } = useAPIClientRequest(ApiClient.Entities.Schedule.revalidateSite);

    const handleRevalidate = useCallback(async () => {
        await to(revStart(campaignId, schedule.id));
    }, [campaignId, revStart, schedule.id]);

    useEffect(() => {
        if (requestState === NetworkRequestState.Success) {
            getSchedules(schedulesCurrentPage, schedulesPerPage, scheduleSearch);
        }
        // eslint-disable-next-line
    }, [requestState]);

    const actions = useMemo(
        () => (
            <Box>
                <IfHasAllPermissions permissions={attachPermissions}>
                    {!schedule.is_file && (
                        <IconButton
                            color="primary"
                            disabled={selectedCount === 0}
                            onClick={attachClick}
                            size="small"
                        >
                            <AddToQueue fontSize="inherit" />
                        </IconButton>
                    )}
                </IfHasAllPermissions>
                <IfHasAllPermissions permissions={attachPermissions}>
                    {!schedule.is_file && (
                        <IconButton
                            color="primary"
                            disabled={selectedCount === 0}
                            onClick={detachClick}
                            size="small"
                        >
                            <RemoveFromQueue fontSize="inherit" />
                        </IconButton>
                    )}
                </IfHasAllPermissions>
                <IfHasAllPermissions permissions={updatePerms}>
                    {!schedule.is_file && (
                        <IconButton
                            onClick={onEditClicked}
                            disabled={schedule.is_default || schedule.disable_flag}
                            size="small"
                        >
                            {editIcon}
                        </IconButton>
                    )}
                </IfHasAllPermissions>
                {schedule.is_file && !isLoading && !schedule.scheduleFile.data.refresh && (
                    <IconButton onClick={handleRevalidate}>{refreshIcon}</IconButton>
                )}
                {schedule.is_file && isLoading && <CircularProgress />}
                <IfHasAllPermissions permissions={deletePerms}>
                    <IconButton
                        color="error"
                        onClick={onDeleteClicked}
                        size="small"
                        disabled={schedule.is_default || schedule.disable_flag}
                    >
                        {deleteIcon}
                    </IconButton>
                </IfHasAllPermissions>
                {schedule.is_file && (
                    <IconButton color="primary" onClick={handleDownload}>
                        {cloudDownloadIcon}
                    </IconButton>
                )}
                {!schedule.is_file && (
                    <IconButton onClick={collapsed.toggle} size="small">
                        {collapsed.value ? (
                            <ExpandLess
                                fontSize="inherit"
                                onClick={() => setExpandedScheduleId(null)}
                            />
                        ) : (
                            <ExpandMore fontSize="inherit" />
                        )}
                    </IconButton>
                )}
            </Box>
        ),
        [
            selectedCount,
            attachPermissions,
            attachClick,
            collapsed.toggle,
            collapsed.value,
            detachClick,
            handleDownload,
            handleRevalidate,
            isLoading,
            onDeleteClicked,
            onEditClicked,
            schedule,
            setExpandedScheduleId,
        ],
    );
    const scheduleFileErrorMessage = schedule.scheduleFile?.data?.message;

    return (
        <Box ref={focusRef}>
            <Stack gap={1} alignItems="center" direction="row" justifyContent="space-between">
                {header}
                {actions}
            </Stack>
            {!schedule.is_file && <Collapse in={collapsed.value}>{rules}</Collapse>}
            {schedule.is_file && (
                <Box sx={{ marginTop: '-1rem' }}>
                    <Typography variant="inherit" fontWeight="-moz-initial" fontSize="small">
                        <Stack gap={1}>
                            <Stack direction="row" gap={1} pt={1}>
                                {schedule.scheduleFile.data.status}
                                {schedule.scheduleFile.data.status === 'Success' && (
                                    <CheckCircleOutlined color="success" />
                                )}
                                {schedule.scheduleFile.data.status === 'Warning' && (
                                    <Tooltip title={scheduleFileErrorMessage ?? ''}>
                                        <Warning color="warning" />
                                    </Tooltip>
                                )}
                                {schedule.scheduleFile.data.status === 'Error' && (
                                    <Tooltip title={scheduleFileErrorMessage ?? ''}>
                                        <Error color="error" />
                                    </Tooltip>
                                )}
                            </Stack>

                            {error && <Alert severity="error">{String(error)}</Alert>}
                        </Stack>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ScheduleListItem;
